"use client";

import { getViewportForBounds, Position, useReactFlow, ViewportPortal } from "@xyflow/react";
import { useEffect, useRef, useState, type RefObject } from "react";
import { absolutePosition } from "@/lib/auto-fuse";
import { assistantEditDiff } from "@/lib/assistant/edit-diff";
import { assistantIdentity, useAssistantStore, type AssistantAppliedEdit } from "@/lib/assistant/store";
import { getDefinition } from "@/lib/catalog";
import { GEOMETRY } from "@/lib/canvas-geometry";
import { rectifyEndpointWaypoints, roundedPath, routeOrthogonal, simplifyOrthogonal, type Pt } from "@/lib/cable-routing";
import { effectiveDimensions, effectiveTerminals } from "@/lib/effective";
import { isJunction } from "@/lib/node-kind";
import { useSystemStore } from "@/lib/store";
import { endpointPos } from "@/lib/store-helpers";
import type { Cable, System, SystemNode } from "@/lib/types";
import "./assistant-edit-reveal.css";

type Rect = { x: number; y: number; width: number; height: number };
type Highlight = "added" | "changed" | "removed";
type Reveal = {
  event: AssistantAppliedEdit;
  nodes: (Rect & { id: string; kind: Highlight })[];
  cables: { id: string; kind: Highlight; path: string }[];
  bounds: Rect;
};

function nodeRect(node: SystemNode, system: System): Rect {
  const position = absolutePosition(node, system.nodes);
  if (node.kind === "zone") return { ...position, width: node.width, height: node.height };
  const definition = getDefinition(node.definitionId, node.customDefinition);
  const dimensions = isJunction(node, definition)
    ? { width: GEOMETRY.JUNCTION_HALF * 2, height: GEOMETRY.JUNCTION_HALF * 2 }
    : definition ? effectiveDimensions(definition, node) : { width: 40, height: 40 };
  return { ...position, ...dimensions };
}

function cablePoints(cable: Cable, system: System): Pt[] {
  const source = endpointPos(cable.fromNodeId, cable.fromTerminalId, system.nodes);
  const target = endpointPos(cable.toNodeId, cable.toTerminalId, system.nodes);
  if (!source || !target) return cable.waypoints ?? [];
  const side = (nodeId: string, terminalId: string, fallback: Position): Position => {
    const node = system.nodes.find(item => item.id === nodeId);
    if (!node || node.kind !== "component") return fallback;
    const definition = getDefinition(node.definitionId, node.customDefinition);
    return (definition && effectiveTerminals(definition, node).find(terminal => terminal.id === terminalId)?.side as Position) || fallback;
  };
  const sourceSide = side(cable.fromNodeId, cable.fromTerminalId, Position.Right);
  const targetSide = side(cable.toNodeId, cable.toTerminalId, Position.Left);
  return cable.waypoints?.length
    ? simplifyOrthogonal([source, ...rectifyEndpointWaypoints(source, sourceSide, target, targetSide, cable.waypoints, {
      skipSource: isJunction(system.nodes.find(node => node.id === cable.fromNodeId)),
      skipTarget: isJunction(system.nodes.find(node => node.id === cable.toNodeId)),
    }), target])
    : routeOrthogonal(source, target, sourceSide, targetSide);
}

function geometry(event: AssistantAppliedEdit, root: HTMLElement): Reveal | null {
  const diff = assistantEditDiff(event.before, event.after);
  const points: Pt[] = [];
  const nodes: Reveal["nodes"] = [];
  const cables: Reveal["cables"] = [];
  for (const change of diff.nodes) {
    const node = change.after ?? change.before;
    const rect = nodeRect(node, change.kind === "removed" ? event.before : event.after);
    nodes.push({ ...rect, id: node.id, kind: change.kind });
    points.push({ x: rect.x - 16, y: rect.y - 16 }, { x: rect.x + rect.width + 16, y: rect.y + rect.height + 16 });
  }
  // Match the actual rendered cable, including its obstacle routing. Removed
  // cables no longer have a DOM path, so retain their previous stored route.
  const renderedPaths = new Map(Array.from(root.querySelectorAll(".react-flow__edge"), edge => [
    edge.getAttribute("data-id"), edge.querySelector(".react-flow__edge-path")?.getAttribute("d"),
  ]));
  for (const change of diff.cables) {
    const cable = change.after ?? change.before;
    const route = cablePoints(cable, change.kind === "removed" ? event.before : event.after);
    const path = (change.kind !== "removed" && renderedPaths.get(cable.id)) || roundedPath(route, 14);
    if (!path) continue;
    cables.push({ id: cable.id, kind: change.kind, path });
    points.push(...route);
  }
  if (!points.length) return null;
  const x = Math.min(...points.map(point => point.x));
  const y = Math.min(...points.map(point => point.y));
  return { event, nodes, cables, bounds: {
    x, y,
    width: Math.max(1, Math.max(...points.map(point => point.x)) - x),
    height: Math.max(1, Math.max(...points.map(point => point.y)) - y),
  } };
}

/** A brief, non-interactive reveal of the actual committed edit. */
export function AssistantEditReveal({ canvasRef, touch }: {
  canvasRef: RefObject<HTMLDivElement | null>;
  touch: boolean;
}) {
  const event = useAssistantStore(state => state.appliedEdit);
  const system = useSystemStore(state => state.system);
  const { getViewport, setViewport } = useReactFlow();
  // Remounts (such as opening a print preview) must not replay an old edit.
  const seen = useRef(event?.id);
  const [reveal, setReveal] = useState<Reveal | null>(null);

  useEffect(() => {
    if (!event || seen.current === event.id || system !== event.after) return;
    seen.current = event.id;
    const root = canvasRef.current;
    if (!root || event.identity !== assistantIdentity()) return;
    let expiry: ReturnType<typeof setTimeout> | undefined;
    let cameraMoving = false;
    let cancelled = false;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const start = window.setTimeout(() => {
      if (event.identity !== assistantIdentity() || useSystemStore.getState().system !== event.after) return;
      const next = geometry(event, root);
      if (!next) return;
      const viewport = getViewport();
      const canvasBounds = root.getBoundingClientRect();
      const { width, height } = canvasBounds;
      // The mobile conversation remains reachable as a short strip below the
      // revealed drawing. Its measured height includes the safe-area inset.
      const dock = touch ? document.querySelector<HTMLElement>(".editor-assistant-docked") : null;
      const reserve = dock ? Math.max(0, canvasBounds.bottom - dock.getBoundingClientRect().top) : 0;
      const desktop = !touch ? document.querySelector<HTMLElement>(".editor-assistant-desktop") : null;
      const sidebarWidth = desktop ? Math.max(0, canvasBounds.right - desktop.getBoundingClientRect().left) : 0;
      const visibleWidth = Math.max(0, width - sidebarWidth);
      const visibleHeight = Math.max(1, height - reserve);
      if (visibleWidth > 64 && visibleHeight > 64) {
        const visible = {
          x: -viewport.x / viewport.zoom,
          y: -viewport.y / viewport.zoom,
          width: visibleWidth / viewport.zoom,
          height: visibleHeight / viewport.zoom,
        };
        const b = next.bounds;
        const margin = 32 / viewport.zoom;
        // Make a small edit readable when the drawing was zoomed far out.
        if (viewport.zoom < 0.55 || b.x < visible.x + margin || b.y < visible.y + margin ||
            b.x + b.width > visible.x + visible.width - margin || b.y + b.height > visible.y + visible.height - margin) {
          const target = getViewportForBounds(b, visibleWidth, visibleHeight, 0.2, Math.max(viewport.zoom, 1), 0.18);
          cameraMoving = !reducedMotion;
          void setViewport(target, { duration: reducedMotion ? 0 : 460 }).finally(() => { cameraMoving = false; });
        }
      }
      setReveal(next);
      expiry = setTimeout(() => setReveal(null), 2800);
    }, touch && !reducedMotion ? 440 : 40);
    // A user gesture or a subsequent edit takes priority over the reveal.
    const stopCamera = () => {
      if (!cameraMoving) return;
      cameraMoving = false;
      void setViewport(getViewport(), { duration: 0 });
    };
    const cancel = () => {
      if (cancelled) return;
      cancelled = true;
      window.clearTimeout(start);
      stopCamera();
      setReveal(null);
    };
    root.addEventListener("pointerdown", cancel, { once: true, capture: true });
    root.addEventListener("wheel", cancel, { once: true, capture: true, passive: true });
    return () => {
      window.clearTimeout(start);
      if (expiry) window.clearTimeout(expiry);
      stopCamera();
      root.removeEventListener("pointerdown", cancel, true);
      root.removeEventListener("wheel", cancel, true);
    };
  }, [canvasRef, event, getViewport, setViewport, system, touch]);

  if (!reveal || system !== reveal.event.after || event?.id !== reveal.event.id) return null;
  return (
    <ViewportPortal>
      <svg key={reveal.event.id} className="assistant-edit-reveal" aria-hidden="true" data-assistant-edit-reveal>
        {reveal.cables.map(cable => (
          <path key={cable.id} className={`assistant-edit-reveal__cable assistant-edit-reveal--${cable.kind}`} d={cable.path} />
        ))}
        {reveal.nodes.map(node => (
          <rect key={node.id} className={`assistant-edit-reveal__node assistant-edit-reveal--${node.kind}`}
            x={node.x - 8} y={node.y - 8} width={node.width + 16} height={node.height + 16} rx={12} />
        ))}
      </svg>
    </ViewportPortal>
  );
}
